import './UserNotifications.scss';

import { mdiBellOutline } from '@mdi/js';
import * as signalR from '@microsoft/signalr';
import { useQueryClient } from '@tanstack/react-query';
import type { PopoverProps } from 'antd';
import { message, notification, Popover } from 'antd';
import { NOTIFICATIONS_ROOT } from 'app_config';
import { useSystemAnnouncements } from 'common/AnnouncementsProvider';
import type { SignalRNotification } from 'common/models';
import {
  getCompanyAndUserInfoKeyBuilder,
  getCurrentAnnouncementsKeyBuilder,
  getFeatureFlagsKeyBuilder,
  useAddRealtimeNotification,
  useGetCompanyAndUserInfoQuery,
  useGetCurrentAnnouncementsQuery,
  useGetNotificationsQuery,
  useMarkNotificationAsSeenMutation
} from 'common/queries';
import useAuth from 'common/useAuth';
import Button from 'components/Button/Button';
import Icon from 'components/Icon/Icon';
import NotificationsList from 'components/Notifications/NotificationsList';
import { getVisibleAnnouncements } from 'helpers/announcementsHelper';
import { handleApiError } from 'helpers/errorHandling';
import type { DeviceInvalidationReason } from 'helpers/notificationsHelper';
import {
  getDeviceInvalidationReason,
  getNotificationIcon,
  getNotificationLink,
  isNotificationLinkActive
} from 'helpers/notificationsHelper';
import { getAccessToken, getDeviceId } from 'helpers/tokenManager';
import { useCustomTranslation } from 'helpers/translationHelper';
import { getCodebooksQueryKeyBuilder } from 'pages/settings/codebooks/queries';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AnnouncementsList } from '..';

export const NUMBER_OF_DAYS = 7;

// Longer than antd's 3s default: the toast has to stay readable across the redirect to the login page.
const DEVICE_INVALIDATED_MESSAGE_DURATION_SECONDS = 6;

interface Props {
  placement?: PopoverProps['placement'];
  onOpenChange?: (open: boolean) => void;
  label?: string;
}

const UserNotifications: React.FC<Props> = ({
  placement = 'bottomRight',
  onOpenChange,
  label
}): React.ReactElement => {
  const { t, tCommon } = useCustomTranslation();

  const navigate = useNavigate();
  const { logout } = useAuth();
  const { systemAnnouncements } = useSystemAnnouncements();

  const [showNotificationsList, setShowNotificationsList] = useState(false);

  useEffect(() => {
    onOpenChange?.(showNotificationsList);
  }, [onOpenChange, showNotificationsList]);

  const queryClient = useQueryClient();
  const companyAndUserQuery = useGetCompanyAndUserInfoQuery();
  const { data, isFetching, fetchNextPage, hasNextPage } = useGetNotificationsQuery();
  const announcmentsQuery = useGetCurrentAnnouncementsQuery();

  const markNotificationAsSeenMutation = useMarkNotificationAsSeenMutation();
  const addRealTimeNotification = useAddRealtimeNotification();

  const user = companyAndUserQuery.data?.user;

  const notifications = data?.pages?.reduce(
    (acc: SignalRNotification[], page) => [...acc, ...page.items],
    []
  );

  const handleMarkNotificationsAsSeen = useCallback(() => {
    const notificationsToMarkAsSeen =
      notifications
        ?.filter((notification) => !notification.seenAt)
        .map((notification) => notification.id) || [];

    notificationsToMarkAsSeen.length > 0 &&
      markNotificationAsSeenMutation.mutate(notificationsToMarkAsSeen);
  }, [markNotificationAsSeenMutation, notifications]);

  const renderNotification = useCallback(
    (signalRNotification: SignalRNotification) => {
      notification.open({
        title: signalRNotification.title,
        description: signalRNotification.message,
        icon: getNotificationIcon(signalRNotification.type),
        onClick: () =>
          isNotificationLinkActive(signalRNotification.type) &&
          getNotificationLink(signalRNotification, navigate),
        placement: 'bottomRight',
        className: 'RealtimeNotification'
      });
    },
    [navigate]
  );

  // Kept out of handleReceivedNotification on purpose: the handler's `notification` parameter
  // shadows antd's `notification` API, so the toast has to be raised from this scope.
  const renderDeviceInvalidated = useCallback(
    (reason: DeviceInvalidationReason) => {
      // A forced logout is a system toast, not a user notification: antd's `notification`
      // (the bottom-right feed) is reserved for those, so use `message` like error toasts do.
      // The reason copy is a self-contained sentence, so it needs no separate title.
      void message.warning(
        tCommon(`user_device_invalidated_${reason}`),
        DEVICE_INVALIDATED_MESSAGE_DURATION_SECONDS
      );
    },
    [tCommon]
  );

  const handleReceivedNotification = useCallback(
    async (notification: SignalRNotification) => {
      // The single-device policy invalidated some of this user's devices. If ours is among them,
      // log out right away with a reason-specific message, instead of waiting for the next request
      // to fail with a 401 (which logs out silently). Other devices' invalidations are not our business.
      if (notification.type === 'UserDeviceInvalidated') {
        const reason = getDeviceInvalidationReason(notification, getDeviceId());

        if (reason) {
          renderDeviceInvalidated(reason);
          await logout();
        }

        return;
      }

      if (notification.type === 'CodebooksChanged') {
        await queryClient.invalidateQueries(getCodebooksQueryKeyBuilder());
      } else if (
        notification.type === 'CompanySettingsChanged' ||
        notification.type === 'CompanyConfigurationChanged' ||
        (notification.type === 'UserChanged' && notification.data.ChangedUserId === user?.id)
      ) {
        await queryClient.invalidateQueries(getCompanyAndUserInfoKeyBuilder());
      } else if (notification.type === 'FeatureFlagsChanged') {
        await queryClient.invalidateQueries(getFeatureFlagsKeyBuilder());
      } else if (notification.type === 'AnnouncementChanged') {
        await queryClient.invalidateQueries(getCurrentAnnouncementsKeyBuilder());
      } else {
        notification.isVisibleForUser && addRealTimeNotification(notification);
      }

      notification.isVisibleForUser && renderNotification(notification);
      showNotificationsList && markNotificationAsSeenMutation.mutate([notification.id]);
    },
    [
      queryClient,
      showNotificationsList,
      renderNotification,
      renderDeviceInvalidated,
      logout,
      addRealTimeNotification,
      markNotificationAsSeenMutation,
      user?.id
    ]
  );

  useEffect(() => {
    const connect = new signalR.HubConnectionBuilder()
      .withUrl(`${NOTIFICATIONS_ROOT}/user/realtime`, {
        accessTokenFactory: () => getAccessToken()
      })
      .withAutomaticReconnect()
      .build();

    connect
      .start()
      .then(() => connect.on('sendNotification', handleReceivedNotification))
      .catch((e) => handleApiError(e));

    // TODO: possible to handle onclose and onreconnecting
    connect.onreconnected(() => {
      connect.off('sendNotification'); // Clear any existing subscriptions
      connect.on('sendNotification', handleReceivedNotification); // Re-subscribe
    });

    return () => {
      const clear = async () => {
        connect.off('sendNotification');
        // A breakpoint can unmount this trigger while the connection is still starting.
        await connect.stop();
      };

      clear().catch((e) => handleApiError(e));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const notificationsForUser = notifications || [];

  const filteredAnnouncements = getVisibleAnnouncements(
    announcmentsQuery.data?.filter((x) => x.type !== 'InfoModal') || []
  );

  const totalUnreadCount =
    systemAnnouncements.length +
    (data?.pages[0]?.unreadCount || 0) +
    (filteredAnnouncements.length || 0);

  return (
    <div className="UserNotifications">
      <Popover
        content={
          <div>
            <div className="UserNotifications_list">
              {[...filteredAnnouncements, ...systemAnnouncements].length > 0 && (
                <AnnouncementsList
                  announcements={[...filteredAnnouncements, ...systemAnnouncements]}
                />
              )}
              {notificationsForUser.length > 0 && (
                <NotificationsList
                  notifications={notificationsForUser}
                  onClose={() => setShowNotificationsList(false)}
                />
              )}
            </div>
            <div className="UserNotifications_showMore">
              <Button
                type="link"
                onClick={() => fetchNextPage()}
                loading={isFetching}
                disabled={!hasNextPage}
              >
                {t('misc.loadMore')}
              </Button>
            </div>
          </div>
        }
        trigger="click"
        open={showNotificationsList}
        onOpenChange={(open) => {
          setShowNotificationsList(open);
          open && handleMarkNotificationsAsSeen();
        }}
        placement={placement}
        classNames={{
          root: `UserNotifications_content ${
            filteredAnnouncements.length > 0 && filteredAnnouncements[0].type === 'Info'
              ? 'InfoArrow'
              : ''
          }
          ${
            filteredAnnouncements.length > 0 && filteredAnnouncements[0].type === 'Warning'
              ? 'WarningArrow'
              : ''
          }
          `
        }}
      >
        <span>
          <Button
            className="notification-menu-button"
            type="link"
            aria-label={tCommon('navigation_notifications', { defaultValue: 'Notifikace' })}
            aria-expanded={showNotificationsList}
          >
            <span className="UserNotifications_icon">
              <Icon path={mdiBellOutline} />
              {totalUnreadCount > 0 && (
                <span className="UserNotifications_badge">
                  {totalUnreadCount > 9 ? `9+` : totalUnreadCount}
                </span>
              )}
            </span>
            {label && <span>{label}</span>}
          </Button>
        </span>
      </Popover>
    </div>
  );
};

export default UserNotifications;
